<template>
  <div>
    <div class="r-applications container position-relative">
    <h2 class="mb-5">Manage applications</h2>
      <div class="row" v-show="isFiltersVisible">
        <div class="col-md-7"></div>
        <div class="col-md-2">
          <base-select
            name="year"
            label
            v-model="year"
            :options="userApplicationsYears"
            col1="col-sm-0"
            col2="col-sm-12"
            select-label="name"
          ></base-select>
        </div>
        <div class="col-md-3">
          <base-select
            name="company"
            label
            v-model="company"
            :options="userCompanies"
            col1="col-sm-0"
            col2="col-sm-12"
            select-label="name"
          ></base-select>
        </div>
      </div>
      <table
        class="table table-overview table-bordered"
        :class="{ 'no-display': !overviewVisible }"
        style="width: 300px"
      >
        <tbody>
          <tr>
            <td>Total added:</td>
            <td>
              <span>{{ volumeByWeightUnit(getTotalAdded) }} {{getWeightUnit}}</span>
            </td>
          </tr>
          <tr>
            <td>Sum in submissions:</td>
            <td>
              <span>{{ volumeByWeightUnit(getSumInSubmissions) }} {{getWeightUnit}}</span>
            </td>
          </tr>
          <tr>
            <td>Remaining:</td>
            <td>
              <span>{{ volumeByWeightUnit(getRemainingMaterials) }} {{getWeightUnit}}</span>
            </td>
          </tr>
          <tr :class="{ 'is-high-qty': isHighQty }">
            <td>Sum in sliders:</td>
            <td>
              <span>{{ volumeByWeightUnit(getSumInSliders) }} {{getWeightUnit}}</span>
            </td>
          </tr>
        </tbody>
      </table>
      <router-view
        :computedApplications="getComputedApplications"
        :groupedUserCompositions="getGroupedUserCompositions"
        :totalAdded="getTotalAdded"
        :sumInSubmissions="getSumInSubmissions"
        :remainingMaterials="getRemainingMaterials"
        :sumInSliders="getSumInSliders"
        :company="company"
        :year="year"
        @isHighQty="handleIsHighQty"
      />
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import applicationStore from './applications.store';
  import BaseSelect from '@/components/inputs/select/BaseSelect';

  export default {
    data() {
      return {
        year: new Date().getFullYear().toString(),
        company: {},
        scrollTop: 0,
        compositionPosition: 250,
        isHighQty: false,
        isFiltersVisible: true,
      };
    },

    computed: {
      ...mapState({
        userCompanies: state => state.userCompanies.loggedUserCompanies,
        userCompanyCompositions: state => state.userCompositions.companyCompositions,
        userApplications: state => state.userApplications.applications,
        currentUserApplications: state => state.userApplications.current,
        userSubmissions: state => state.userSubmissions.data,
        userApplicationsYears: state => state.userApplications.years,
      }),
      getWeightUnit() {
        return this.$store.getters['user/getWeightUnit'];
      },
      getCompositionNameMap() {
        const compositionNameMap = {};
        this.userCompanyCompositions
          .forEach(composition => compositionNameMap[composition.id] = composition.name);
        return compositionNameMap;
      },
      computeCompositionsApplicationsData() {
        const applicationLinksSum = links => links.reduce((a, b) => a + b.volume, 0);

        const computedCompositionsObj = {};
        this.userCompanyCompositions.forEach((composition) => {
            computedCompositionsObj[composition.id] = {
              id: composition.id,
              companyId: composition.company_id,
              name: composition.name,
              usedVolume: 0
            };
        });


        const computedApplicationsObj = {};
        this.userApplications.forEach((application) => {
          if (computedCompositionsObj[application.composition_id]) {
            computedCompositionsObj[application.composition_id].usedVolume += applicationLinksSum(application.applications);
            computedApplicationsObj[application.id] = {
              id: application.id,
              companyId: computedCompositionsObj[application.composition_id].companyId,
              compositionId: computedCompositionsObj[application.composition_id].id,
              compositionName: computedCompositionsObj[application.composition_id].name,
              applicationsLink: application.applications,
              applicationLinksSum: applicationLinksSum(application.applications),
              createdAt: application.createdAt
            };
          }
        });

        return {
          computedApplicationsObj: this.filterByYears(computedApplicationsObj),
          computedCompositionsObj
        };
      },
      getComputedApplications() {
        const { computedApplicationsObj } = this.computeCompositionsApplicationsData;
        return this.filterByYears(computedApplicationsObj);
      },
      getComputedCompositions() {
        const { computedCompositionsObj } = this.computeCompositionsApplicationsData;
        return computedCompositionsObj;
      },
      getGroupedUserCompositions() {
        const userSubmissionsObj = {};
        this.userSubmissions.forEach((submission) => {
          userSubmissionsObj[submission.composition_id] = {
            id: submission.composition_id,
            name: this.getCompositionNameMap[submission.composition_id],
            companyId: submission.company_id,
            totalVolume: 0
          };
        });

        this.userSubmissions.forEach((submission) => {
          userSubmissionsObj[submission.composition_id].totalVolume += submission.volume_net;
        });

        const compositions = this.getComputedCompositions;
        Object.keys(compositions).forEach(key => {
          if (userSubmissionsObj[key]) {
            userSubmissionsObj[key].remainingVolume = userSubmissionsObj[key].totalVolume - compositions[key].usedVolume;
          }
        });

        return this.filterByYears(userSubmissionsObj);
      },
      getTotalAdded() {
        let total = 0;
        Object.keys(this.getComputedApplications)
          .forEach(key => total += this.getComputedApplications[key].applicationLinksSum);
        return total;
      },
      getSumInSubmissions() {
        let total = 0;
        Object.keys(this.getGroupedUserCompositions)
          .forEach(key => total += this.getGroupedUserCompositions[key].totalVolume);
        return total;
      },
      getRemainingMaterials() {
        return this.getSumInSubmissions - this.getTotalAdded;
      },
      getSumInSliders() {
        let sum = 0;
        this.currentUserApplications.applications.forEach(e => sum += +e.volume);
        return sum;
      },
      overviewVisible() {
        return this.scrollTop > this.compositionPosition;
      }
    },

    watch: {
      userCompanies(newValue) {
        this.company = newValue[0];
      },
      '$route' (to) {
        this.isFiltersVisible = to.name !== 'user.applications.edit';
      }
    },

    methods: {
      ...mapActions({
        getUserCompanyCompositions: 'userCompositions/companyCompositions',
        getApplications: 'applications/getApplications',
        getApplicationLinks: 'userApplications/getApplicationLinks',
        getUserSubmissions: 'userSubmissions/getSubmissions',
        getUserCompanies: 'userCompanies/getCompanies',
      }),
      volumeByWeightUnit(volume) {
        if (this.getWeightUnit === 'ton') {
          return volume / 1000;
        }
        return volume;
      },
      handleIsHighQty(isHighQty) {
        this.isHighQty = isHighQty;
      },
      filterByYears(objectToFilter) {
        if (this.company && this.company.id) {
          const filteredCompositions = {};
          Object.keys(objectToFilter)
            .forEach((key) => {
              if (objectToFilter[key].companyId === this.company.id) {
                filteredCompositions[key] = objectToFilter[key];
              }
            });

            return filteredCompositions;
        }
        return objectToFilter;
      }
    },

    beforeMount() {
      this.$store.registerModule('applicationModule', applicationStore);
    },

    async mounted() {
      this.getUserCompanyCompositions();
      this.getApplications();
      this.getApplicationLinks();
      this.getUserSubmissions();
      const companies = await this.getUserCompanies(this.$store.getters['user/getLoggedInUserId']);

      const { company } = this.$route.params;
      if (company) {
        this.$nextTick(() => {
          this.company = company;
        });
      } else {
        this.company = companies[0] || {};
      }

      $(window).scroll(() => {
        this.scrollTop = $(window).scrollTop();
      });
    },

    beforeDestroy() {
        this.$store.unregisterModule('applicationModule');
    },

    components: {
      'base-select': BaseSelect,
    }
  };
</script>

<style lang="scss">
  .is-high-qty {
    font-weight: 900;
    color: red;
  }
</style>
